import { createApi } from "@reduxjs/toolkit/query/react";

import { advisorService } from "./advisorRestService";
import {
  IStartAdvisorSessionRequest,
  IStartAdvisorSessionResponse,
  IChooseAdvisorChoiceRequest,
  IChooseAdvisorChoiceResponse,
  IFetchAdvisorSessionRequest,
  IAdvisorSessionStateResponse,
} from "@types";

// Separate backend from the Django TOM API, so it needs its own base URL instead of
// `${REACT_APP_BASE_URL}/company`.
export const advisorApi = createApi({
  reducerPath: "advisorApi",
  baseQuery: advisorService({
    baseUrl: `${process.env.REACT_APP_ADVISOR_BASE_URL}`,
  }),
  tagTypes: ["AdvisorSession"],
  endpoints: builder => ({
    startAdvisorSession: builder.mutation<
      IStartAdvisorSessionResponse,
      IStartAdvisorSessionRequest
    >({
      query: ({ offer_id, company_id, ...overlay }) => ({
        url: `/advisor/sessions`,
        method: "POST",
        body: { offer_id, company_id, ...overlay },
      }),
    }),
    chooseAdvisorChoice: builder.mutation<
      IChooseAdvisorChoiceResponse,
      IChooseAdvisorChoiceRequest
    >({
      query: ({ session_id, choice_id, text }) => ({
        url: `/advisor/sessions/${session_id}/choose`,
        method: "POST",
        body: text ? { choice_id, text } : { choice_id },
      }),
      // Scoped by id: an unscoped ["AdvisorSession"] tag invalidated every cached session, so a
      // choose here refetched whatever other session a subscribed query still held.
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "AdvisorSession" as const, id: session_id },
      ],
    }),
    fetchAdvisorSession: builder.query<
      IAdvisorSessionStateResponse,
      IFetchAdvisorSessionRequest
    >({
      query: ({ session_id }) => ({
        url: `/advisor/sessions/${session_id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { session_id }) => [
        { type: "AdvisorSession" as const, id: session_id },
      ],
    }),
  }),
});

export const {
  useStartAdvisorSessionMutation,
  useChooseAdvisorChoiceMutation,
  useFetchAdvisorSessionQuery,
  useLazyFetchAdvisorSessionQuery,
} = advisorApi;
